import z from 'zod';

/**
 * Lead pipeline status.
 *
 * Ordered from coldest to won — `LEAD_STATUS_ORDER` in `@hero/core`
 * mirrors this order and is what `deriveLeadStatus` walks, so a lead
 * never rewinds when several funnel rules match at once.
 *
 * The three statuses in the middle (`engaged`, `activated`) are new, and
 * they exist because the old enum forced a lie: `markSignedUp` set `won`
 * at signup, which is free — so the pipeline reported revenue that did
 * not exist. `won` now means money arrived, and `activated` is the state
 * that used to be missing between "an account exists" and "they paid".
 */
export const leadStatusSchema = z.enum([
  /** Captured, nobody has reached out. */
  'new',
  /** We reached out — a call, a template. Says nothing about interest. */
  'contacted',
  /** They answered. The first signal that comes from them, not from us. */
  'engaged',
  /** Real intent: an account, or waybill issuance switched on. */
  'qualified',
  /** Using the product — an account plus a first real document or order. */
  'activated',
  /** Money arrived. Requires `convertedOrg`. */
  'won',
  /** Not a fit — a human judgement, never derived from a step. */
  'unqualified',
  /** Lost — including an explicit STOP. */
  'lost',
  /** Unreachable / abandoned. A human judgement. */
  'dead',
]);

export type LeadStatus = z.infer<typeof leadStatusSchema>;
